/* global REMOTE_ORIGIN */

const Brands = require('../brands.js');
const Errors = require('../errors.js');
const Events = require('../events.js');
const Fields = require('../fields.js');
const Format = require('../format.js');
const Message = require('../message.js');
const Tokenization = require('../tokenization.js');
const Style = require('../style.js');
const Validator = require('../validator.js');

/* Constructor */
function Controller (view) {
  var self = this;
  self.view = view;

  self.type = null;  // the type of this field
  self.value = null; // the current value of this input (updated on input)
  self.previousValue = null; // the previous value of this input (updated on input)
  self.brand = Brands.Names.Unknown; // applies to card number and cvv
  self.acceptedBrands = null; 
  self.validation = null;
  self.styles = {};
  self.state = { 
    dirty: false, // true if input has been updated
    empty: null,
    complete: null,
    error: null,
  };

  // Holds validation status of all fields during tokenization. 
  self.tokenValidation = {};
  self.tokenValidation[Fields.CARD_NUMBER] = null;
  self.tokenValidation[Fields.CVV] = null;
  self.tokenValidation[Fields.EXPIRY] = null;

  // add methods from other modules (not called directly to maintain testability)
  self.getToken = Tokenization.getToken.bind(Tokenization);

  // link message eventListener callback to the view immediately
  self.view.bind('message', function(event) {
    self.receiveMessage(event);
  });

  self.view.render('init');

  self.view.bind(Events.focus, () => {
    self.view.render('noOutline');
  });

  // add hidden inputs to capture and pass along autofill values
  self.view.render('initAutofillInputs');
  self.view.bind(Events.input, e => {
    if (e.target.value == '') {
      self.view.render('resetAutofillInputs');
    }
  });

  self.type = self.view.type;
}

Controller.prototype.updateState = function(state, status) {
  const self = this;

  if (state === 'empty') {
    if (self.state.empty !== status) {
      self.state.empty = status;
      self.view.trigger(Events.empty);

      if (self.state.empty === true) {

        self.view.render('addCssClass', 'empty');
      } else {
        self.view.render('removeCssClass', 'empty');
      }
    }

  } else if (state === 'complete') {
    if (self.state.complete !== status) {
      self.state.complete = status;
      if (self.state.complete === true) {
        self.state.error = false;
      }
      self.view.trigger(Events.complete);

      if (self.state.complete === true) {
        self.view.render('addCssClass', 'complete');
        self.view.render('removeCssClass', 'error');
      } else {
        self.view.render('removeCssClass', 'complete');
        self.view.render('removeAutofillStyle');
      }
    }

  } else if (state === 'error') {
    if (self.state.error !== status) {
      if (!self.state.dirty) {
        return;
      }  
      self.state.error = true;  
      self.view.trigger(Events.error);

      self.view.render('addCssClass', 'error');
    }
  }

  const msg = Message.updateIFrame(self.type, self.view.height + 'px');
  self.view.postMessage(msg, 'source');
};

Controller.prototype.updateBrand = function(details) {
  const self = this;

  if (self.type !== Fields.CVV) {
    self.view.render('error', 'updateBrand() should only be called on the cvv');
    return false;
  }

  self.brand = details.brand;
  self.fieldHasChanged(false);
};

Controller.prototype.sendCurrentBrand = function(details) {
  const self = this;
  self.view.postMessage(Message.brandChange(self.brand), details.asker);
};

Controller.prototype.changeValue = function(details) {
  const self = this;
  if (details.autofill) {
    self.view.render('autofillStyle');
  }
  self.value = details.newValue;
  self.fieldHasChanged(false);
};

Controller.prototype.triggerValidation = function(oldValidation) {
  const self = this;

  if (oldValidation === null && self.validation.isValid) {
    self.updateState('complete', true);
  } else if (oldValidation === null && self.validation.error) {
    self.updateState('error');
  } else if (oldValidation.error && self.validation.isValid) {
    self.updateState('complete', true);
  } else if (oldValidation.error && self.validation.error.message !== oldValidation.error.message) {
    self.updateState('error');
  } else if (oldValidation.isValid && self.validation.error) {
    self.updateState('complete', false); // incomplete, then error. in case of input not dirty
    self.updateState('error');
  }
};

Controller.prototype.checkForBrandUpdate = function() {
  const self = this;
  const oldBrand = self.brand;
  const cardBrand = Brands.cardBrand(self.value);
  self.brand = cardBrand.brand;

  if (oldBrand !== self.brand) {
    self.view.trigger(Events.brand);
    if (self.cvvIsPresent()) {
      const msg = Message.brandChange(self.brand);
      self.view.postMessage(msg, Fields.CVV);
    }
  }
  return cardBrand;
};

Controller.prototype.validate = function() {
  const self = this;
  let validator = new Validator();
  if (self.type === Fields.CARD_NUMBER) {
    self.validation = validator.validateCardNumber(self.value, self.acceptedBrands);
  } else if (self.type === Fields.CVV) {
    self.validation = validator.validateCvv(self.value, Brands.cardBrandByName(self.brand));
  } else {
    self.validation = validator.validateExpiry(self.value);
  }
};

// Note: Cursor positioning is finicky and behaves differently in different 
// browsers, ensures changes to related code are tested in all supported browsers.
Controller.prototype.fieldHasChanged = function(shouldUpdateCursor) {
  const self = this;
  const oldValue = self.value;

  let cardBrand;
  if (self.type === Fields.CARD_NUMBER) {
    cardBrand = self.checkForBrandUpdate();
    self.value = Format.cardNumber(self.value, cardBrand);
  } else if (self.type === Fields.CVV) {
    cardBrand = Brands.cardBrandByName(self.brand);
    self.value = Format.cvv(self.value, cardBrand);
  } else if (self.type === Fields.EXPIRY) {
    self.value = Format.expiry(self.value);
  }

  let cursorPosition = self.view.getCursorPosition();
  self.view.trigger(Events.value, self.value);

  if (shouldUpdateCursor && self.value) {
    // For older Android Browsers we need to handle entering input for the first time
    if (!self.previousValue) {
      cursorPosition = self.value.length;
    }

    cursorPosition = Format.cursorPosition(cursorPosition, oldValue, self.value);
    self.view.setCursorPosition(cursorPosition);
  }

  const oldValidation = self.validation;
  self.validate();
  self.triggerValidation(oldValidation);

  self.updateState('empty', !self.value);
};


Controller.prototype.addStyles = function(styles) {
  const self = this;

  const errors = Style.validateStyleArgument(styles);
  if (errors.length > 0) {
    errors.forEach(errorMsg => {
      self.view.render('error', errorMsg);
    });
  }

  self.styles = styles;
  self.view.render('addStyles', styles);
  self.view.render('addCssClass', 'base');
};

Controller.prototype.removeStyles = function() {
  const self = this;

  self.styles = null;
  self.view.render('removeStyles', null);
  self.view.render('removeCssClass', 'base');
};

// Updates view and posts message to iframe based on details passed in to
// #create() library method call.
Controller.prototype.create = function(details) {
  const self = this;

  self.registerCallbacks();

  if (details.style) {
    self.addStyles(details.style);
  }

  if (details.placeholder) {
    self.view.render('updatePlaceholder', details.placeholder);
  }

  if (details.brands) {
    if (self.type !== Fields.CARD_NUMBER) {
      self.view.render('error', `Can only pass 'brand' option for ${Fields.CARD_NUMBER} input`);
    } else {
      self.acceptedBrands = details.brands;
    }
  }
  
  self.view.bind(Events.input, function(event) {
    self.previousValue = self.value;
    self.value = event.target.value;
    self.fieldHasChanged(true);
  });

  self.view.bind(Events.blur, (event) => {
    if (self.state.dirty === false && event.target.value !== '') {
      self.state.dirty = true;
      self.validation = null;
      self.fieldHasChanged(false);
    }
  });

  self.updateState('empty', true);

  // if this is the cvv, initialize the current brand
  if (self.type === Fields.CVV) {
    self.view.postMessage(Message.askForBrand(self.type), Fields.CARD_NUMBER);
  }
};

Controller.prototype.registerCallbacks = function() {
  var self = this;

  self.view.bind(Events.blur, () => {
    self.view.postMessage(
      Message.callback(Events.blur, { field: self.type }), 
      'source'
    );
  });

  self.view.bind(Events.focus, () => {
    self.view.postMessage(
      Message.callback(Events.focus, { field: self.type }), 
      'source'
    );
  });

  self.view.bind(Events.empty, () => {
    self.view.postMessage(
      Message.callback(Events.empty, { field: self.type, empty: self.state.empty }), 
      'source'
    );
  });

  self.view.bind(Events.complete, () => {
    self.view.postMessage(
      Message.callback(Events.complete, { field: self.type, complete: self.state.complete }), 
      'source'
    );
  });

  self.view.bind(Events.brand, () => {
    self.view.postMessage(
      Message.callback(Events.brand, { field: self.type, brand: self.brand }),
      'source'
    );
  });

  self.view.bind(Events.error, () => {
    self.view.postMessage(
      Message.callback(Events.error, { 
        field: self.type, 
        type: self.validation.error.type, 
        message: self.validation.error.message,
      }),
      'source'
    );
  });
};

Controller.prototype.cvvIsPresent = function() {
  const self = this;
  return self.view.checkForCvv();
};

// Coordinates validation of all fields for tokenization. This function should 
// only ever be called on a type: CARD_NUMBER instance of the script. First validates
// the card input, and then sends messages to the other iframes requesting 
// validation information. Tokenization is continued once they have responded 
// in #completeFieldValidation().
Controller.prototype.beginFieldValidation = function() {
  var self = this;

  // tokenization should only ever be called on the CARD_NUMBER iframe
  if (self.type !== Fields.CARD_NUMBER) {
    self.view.render('error', 'createToken() should only be called on the card number');
    return false;
  }

  self.state.dirty = true;

  // first check cvv and expiry by posting messages to their iframes
  // need to unset current validation info
  self.tokenValidation[Fields.CARD_NUMBER] = null;
  self.tokenValidation[Fields.EXPIRY] = null;
  if (self.cvvIsPresent()) {
    self.tokenValidation[Fields.CVV] = null;
  } else {
    self.tokenValidation[Fields.CVV] = 'not-present';
  }
  
  const msg = Message.validationRequest();
  self.view.postMessage(msg, Fields.EXPIRY);
  if (self.cvvIsPresent()) {
    self.view.postMessage(msg, Fields.CVV);
  }

  // check card while waiting for a response from cvv and expiry
  self.validate();
  if (self.validation.isValid) {
    self.tokenValidation[Fields.CARD_NUMBER] = {valid: true, value: self.value};
  } else {
    self.updateState('error');
    self.tokenValidation[Fields.CARD_NUMBER] = {valid: false, value: null};
  }

  // cvv and expiry will be validated and return messages of type
  // 'validationResponse', then tokenization will continue.
};

// Validate this field and send the result back to the iframe that requested it 
// (will always be card). This method should ever be called on CARD_NUMBER, since it
// doesn't need to send its validation to any other field.
Controller.prototype.respondToValidationRequest = function() {
  const self = this;

  if (self.type === Fields.CARD_NUMBER) {
    self.view.render(Events.error, 'respondToValidationRequest() should never be called on card');
    return false;
  }

  self.validate();

  let msg;
  if (self.validation.isValid) {
    msg = Message.validationResponse(self.type, true, self.value);
  } else {
    self.view.trigger(Events.error);
    msg = Message.validationResponse(self.type, false, null);
  }
 
  self.view.postMessage(msg, Fields.CARD_NUMBER);
};

// Receive the validation sent by the above method. If everything is all good, 
// we can tokenize, otherwise we return an error.
Controller.prototype.completeFieldValidation = function(details) {
  const self = this;

  if (self.type !== Fields.CARD_NUMBER) {
    self.view.render(Events.error, 'completeFieldValidation() should only ever get called by card');
    return false;  
  }

  const type = details.field;
  self.tokenValidation[type] = { 
    valid: details.valid,
    value: details.value,
  };

  // this block will only be called after all validation has occured 
  if (self.tokenValidation[Fields.CARD_NUMBER] !== null && 
      self.tokenValidation[Fields.CVV] !== null &&
      self.tokenValidation[Fields.EXPIRY] !== null) {

    // if any are invalid
    if (self.tokenValidation[Fields.CARD_NUMBER].valid === false || 
        self.tokenValidation[Fields.CVV].valid === false ||
        self.tokenValidation[Fields.EXPIRY].valid === false) {
      const msg = Message.tokenResponse(Events.error, null, Errors.errorDetails('token', Errors.TokenizationValidationFailed));
      self.view.postMessage(msg, 'source');    
    } else {

      let card = {};
      card[Fields.CARD_NUMBER] = self.value;
      card[Fields.EXPIRY] = self.tokenValidation[Fields.EXPIRY].value;

      if (self.cvvIsPresent()) {
        card[Fields.CVV] = self.tokenValidation[Fields.CVV].value;
      }

      // Need to call tokenization and return that info here
      var callback = function(res) {
        let msg;
        if (res.success) {
          msg = Message.tokenResponse('success', res.code, res.token, res.last4, res.expiryMonth, res.expiryYear);
        } else {
          msg = Message.tokenResponse(Events.error, res.code, res.error);
        }
        self.view.postMessage(msg, 'source');
      };

      self.getToken(card, callback);
    }
  }
};

Controller.prototype.receiveMessage = function(event) {
  const self = this;

  if (!Events.checkOrigin(event, self.view.sourcePageOrigin) && 
      !Events.checkOrigin(event, REMOTE_ORIGIN)) {
    return false;
  }
  
  let message = event.data;
  if (typeof message === 'string' || message instanceof String) {
    try {
      message = JSON.parse(message);
    } catch (err) {
      self.view.render('error', `Could not parse message: ${err.name} - ${err.message}`);
    }

  }
  var name = message.name;
  var details = message.details;

  if (name === 'create') {
    self.view.sourceWindow = event.source;
    self.create(details);
  } else if (name === 'brandChange') {
    self.updateBrand(details);
  } else if (name === 'askForBrand') {
    self.sendCurrentBrand(details);
  } else if (name === 'beginTokenization') { 
    self.beginFieldValidation(details);
  } else if (name === 'validationRequest') {
    self.respondToValidationRequest(details);
  } else if (name === 'validationResponse') {
    self.completeFieldValidation(details);
  } else if (name === 'focus') {
    self.focus();
  } else if (name === 'blur') {
    self.blur();
  } else if (name === 'clear') {
    self.clear();
  } else if (name === 'update') {
    self.update(details);
  } else if (name === 'changeValue') {
    self.changeValue(details);
  } else {
    return false;
  }

  return true;
};

Controller.prototype.focus = function() {
  this.view.focus();
};

Controller.prototype.blur = function() {
  this.view.blur();
};

Controller.prototype.clear = function() {
  this.view.clear();
};

Controller.prototype.update = function(details) {
  const self = this;

  if (details.style) {
    self.removeStyles();
    self.addStyles(details.style);
  }

  if (details.placeholder) {
    self.view.render('updatePlaceholder', details.placeholder);
  }

  if (details.brands) {
    if (self.type !== Fields.CARD_NUMBER) {
      self.view.render('error', `Can only pass 'brand' option for ${Fields.CARD_NUMBER} input`);
    } else {
      self.acceptedBrands = details.brands;
    }
  }
};

module.exports = Controller;



// WEBPACK FOOTER //
// ./app/remote/controller.js